import React, { useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { getFeatures } from './landingContent';
import { 
  InstantPrescreeningIcon, 
  TechInterviewsIcon, 
  FitSoftSkillsIcon, 
  MetaCVIcon
} from './icons';

/**
 * FeaturesSection - Features carousel section
 * 
 * Features:
 * - Horizontal scrolling features carousel
 * - Navigation buttons for scrolling
 * - Responsive design
 * - Hover effects and transitions
 * 
 * @returns {JSX.Element} The features section component
 */
export const FeaturesSection: React.FC = () => {
  const { t } = useTranslation('marketing');
  const features = useMemo(() => getFeatures(t), [t]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getIconComponent = (iconId: string) => {
    switch (iconId) {
      case 'instant-prescreening':
        return <InstantPrescreeningIcon />;
      case 'skill-assessments':
        return <TechInterviewsIcon />;
      case 'fit-soft-skills':
        return <FitSoftSkillsIcon />;
      case 'meta-cv':
        return <MetaCVIcon />;
      default:
        return null;
    }
  };

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      className="w-full bg-white"
      {...getGTMAttributes(createGTMTracking('SECTION_VIEWED', { origin: 'landing_page', target: 'features' }))}
    >
      <div className="max-w-screen-2xl mx-auto">
        <div className="flex flex-col items-center justify-center py-8 sm:py-12 lg:py-20">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="text-center mb-4 sm:mb-12 lg:mb-20 px-6"
          >
            <h2 className="text-[#1B1B28] text-2xl sm:text-3xl lg:text-5xl font-bold leading-tight tracking-tight" style={{ fontFamily: 'var(--font-family-heading)' }}>
              {t('features.title')}
            </h2>
          </motion.div>

          {/* Features Carousel */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.45, delay: 0.05, ease: "easeOut" }}
            className="w-full max-w-full relative"
          >
            {/* Scrollable Features Container */}
            <div
              ref={scrollContainerRef}
              className="overflow-x-auto hide-scrollbar"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              <div className="flex gap-3 sm:gap-4 lg:gap-8 ml-2 py-4 px-6 lg:px-20">
                {features.map((feature, index) => (
                  <motion.article
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.06,
                      ease: "easeOut"
                    }}
                    // whileHover removed: on touch devices framer-motion
                    // treats touchstart as a hover-in and the translate kicks
                    // in mid-gesture, claiming the touch and breaking vertical
                    // pan-through. CSS hover:shadow-lg / hover:border below
                    // still provides the desktop visual.
                    className="flex flex-col items-start bg-white p-4 sm:p-6 lg:p-7 rounded-2xl border-2 border-[#E1D2DA] hover:shadow-lg transition-all duration-300 hover:border-[#DCB3FF] w-[280px] max-w-[calc(100vw-3rem)] sm:w-[320px] lg:w-[360px] sm:max-w-none flex-shrink-0"
                  >
                    {/* Icon */}
                    <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 flex-shrink-0 mb-5 sm:mb-6 lg:mb-7">
                      {getIconComponent(feature.id)}
                    </div>

                    {/* Content */}
                    <div className="flex flex-col space-y-3 sm:space-y-4 lg:space-y-5 flex-1">
                        <h3 
                          className="text-dark-invirtus text-lg sm:text-xl lg:text-3xl font-bold leading-tight" 
                          dangerouslySetInnerHTML={{ __html: feature.title }}
                        />
                      <p className="text-dark-invirtus/70 flex-1">
                        {feature.description}
                      </p>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
              className="flex items-start justify-start gap-3 sm:gap-4 mt-6 sm:mt-8 px-8 lg:px-22"
            >
              <Button
                type="icon"
                intent="light"
                size="lg"
                icon={<ArrowLeft />}
                onClick={scrollLeft}
                className="!border-2 !border-light-lila !bg-white"
                aria-label={t('features.carousel_prev')}
                {...getGTMAttributes(createGTMTracking('CAROUSEL_NAVIGATE', { origin: 'features_section', target: 'carousel_prev' }))}
              />

              <Button
                type="icon"
                intent="light"
                size="lg"
                icon={<ArrowRight />}
                onClick={scrollRight}
                className="!border-2 !border-light-lila !bg-white"
                aria-label={t('features.carousel_next')}
                {...getGTMAttributes(createGTMTracking('CAROUSEL_NAVIGATE', { origin: 'features_section', target: 'carousel_next' }))}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
